import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, KeyRound, Upload, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useFeatures } from "../context/FeatureContext";
import WebhooksSection from "./WebhooksSection";
import IntegrationStatus from "./IntegrationStatus";
import ServerStatusSection from "./ServerStatusSection";
import PmStageConfigSection from "./PmStageConfigSection";
import AssetManagersSection from "./AssetManagersSection";
import MaintenanceVisitTypesSection from "./MaintenanceVisitTypesSection";
import WeatherBackfillSection from "./WeatherBackfillSection";
import BackupSection from "./BackupSection";
import PmBackupSection from "./PmBackupSection";
import LifecycleSanitySection from "./LifecycleSanitySection";
import NotProceedingMigrationSection from "./NotProceedingMigrationSection";
import TaskTemplateSection from "./TaskTemplateSection";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Settings() {
  const { user } = useAuth();
  const settingsCtx = useSettings();
  const refreshBranding = settingsCtx?.refresh || (async () => {});
  const { refresh: refreshFeatures } = useFeatures();
  const [s, setS] = useState(null);
  const [env, setEnv] = useState([]);
  const [logoBusy, setLogoBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data));
    if (user?.role === "admin") {
      api.get("/env").then(r => setEnv(r.data)).catch(() => {});
    }
  }, [user]);

  const save = async () => {
    try {
      const payload = { ...s };
      delete payload.id;
      delete payload.updated_at;
      delete payload.solaredge_api_key_set;
      delete payload.gemini_api_key_set;
      delete payload._feature_catalog;
      const { data } = await api.put("/settings", payload);
      setS(data);
      await refreshBranding();
      await refreshFeatures();
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const logoUrl = `${BASE}${up.data.download_url}`;
      const { data } = await api.put("/settings", { logo_url: logoUrl });
      setS(data);
      await refreshBranding();
      toast.success("Logo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      const { data } = await api.put("/settings", { logo_url: "" });
      setS(data);
      await refreshBranding();
      toast.success("Logo removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLogoBusy(false); }
  };

  const saveEnv = async (key, value) => {
    try {
      await api.put("/env", { key, value });
      toast.success(`${key} updated`);
      const r = await api.get("/env");
      setEnv(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!s) return <div className="p-10 muted font-mono text-xs uppercase tracking-widest">Loading…</div>;

  const u = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setS(prev => ({ ...prev, [k]: v }));
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <div className="label mb-2">Configuration</div>
        <h1 className="font-display text-4xl font-bold">Settings</h1>
        <p className="muted text-sm mt-2">Organisation, UK financial defaults, and server-side credentials.</p>
      </div>

      <section className="swiss-card-flat p-6 mb-6" data-testid="branding-section">
        <div className="label mb-4">Branding</div>
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-32 h-32 border border-[var(--line)] bg-[var(--surface)] flex items-center justify-center" data-testid="logo-preview">
            {s.logo_url ? (
              <img src={s.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-xs muted">No logo</span>
            )}
          </div>
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm muted mb-3">Upload a square or wide logo (PNG, JPG, or WEBP — max 2 MB). It will appear in the sidebar, login screen, customer portal, public quote page, and the quote PDF.</p>
            <div className="flex gap-2">
              <label className="btn-accent cursor-pointer" data-testid="logo-upload-btn">
                <Upload size={14} /> {logoBusy ? "Uploading…" : "Upload logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  data-testid="logo-file-input"
                />
              </label>
              {s.logo_url && (
                <button onClick={removeLogo} className="btn-danger" data-testid="logo-remove-btn" disabled={logoBusy}>
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-[var(--line)]">
          <div className="label mb-3">Colour palette</div>
          <p className="text-xs muted mb-4">
            Applied at runtime to the landing hero, login screen, buttons, chart accents and PDF report headers. Reset a field to use the Flow defaults ({" "}
            <code className="font-mono">#00D6DF</code> accent, <code className="font-mono">#314041</code> primary).
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <ColorPicker label="Accent (call-to-action)" v={s.accent_color || ""}
                         defaultColor="#00D6DF"
                         onChange={(v) => setS(prev => ({ ...prev, accent_color: v }))}
                         t="accent-color" />
            <ColorPicker label="Primary (deep tint)" v={s.primary_color || ""}
                         defaultColor="#314041"
                         onChange={(v) => setS(prev => ({ ...prev, primary_color: v }))}
                         t="primary-color" />
          </div>
          {/* Live preview showing the new colours in context */}
          <div className="mt-5 p-5 border border-[var(--line)]"
               style={{ background: s.primary_color || "#314041" }}
               data-testid="branding-preview">
            <div className="text-white/80 text-[10px] font-mono uppercase tracking-widest mb-2">Live preview</div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 text-white font-mono text-xs uppercase tracking-widest"
                      style={{ background: s.accent_color || "#00D6DF" }}
                      onClick={(e) => e.preventDefault()}>
                Book a demo
              </button>
              <span className="text-white/70 text-xs">Save the form to persist these colours globally.</span>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-[var(--line)]">
          <div className="label mb-3">Outbound email</div>
          <p className="text-xs muted mb-4">
            Override the default <code className="font-mono">RESEND_FROM_EMAIL</code> environment value with an address you&apos;ve verified on Resend (Domains → Add Domain → verify SPF/DKIM). Leave blank to fall back to the env var.
          </p>
          <FieldText label="Email from" v={s.email_from || ""}
                     onChange={u("email_from")} t="email-from"
                     placeholder="noreply@your-domain.com" />
        </div>
      </section>

      <section className="swiss-card-flat p-6 mb-6" data-testid="org-section">
        <div className="label mb-4">Organisation</div>
        <div className="grid md:grid-cols-2 gap-5">
          <FieldText label="Name" v={s.org_name} onChange={u("org_name")} t="org-name" />
          <FieldText label="VAT number" v={s.org_vat_number} onChange={u("org_vat_number")} t="org-vat" />
          <FieldText label="Registration number / MCS" v={s.org_reg_number} onChange={u("org_reg_number")} t="org-reg" />
          <FieldText label="Address" v={s.org_address} onChange={u("org_address")} t="org-addr" />
        </div>
      </section>

      <section className="swiss-card-flat p-6 mb-6" data-testid="finance-section">
        <div className="label mb-4">UK Financial defaults</div>
        <div className="grid md:grid-cols-3 gap-5">
          <FieldNum label="VAT %" v={s.default_vat_pct} onChange={u("default_vat_pct")} t="def-vat" />
          <FieldNum label="Import tariff (p/kWh)" v={s.default_import_tariff_p_per_kwh} onChange={u("default_import_tariff_p_per_kwh")} step={0.1} t="def-import" />
          <FieldNum label="SEG export (p/kWh)" v={s.default_seg_tariff_p_per_kwh} onChange={u("default_seg_tariff_p_per_kwh")} step={0.1} t="def-seg" />
          <FieldNum label="Self-consumption %" v={s.default_self_consumption_pct} onChange={u("default_self_consumption_pct")} t="def-sc" />
          <FieldNum label="Energy inflation %" v={s.default_annual_inflation_pct} onChange={u("default_annual_inflation_pct")} step={0.1} t="def-infl" />
          <FieldNum label="Degradation %/yr" v={s.default_panel_degradation_pct} onChange={u("default_panel_degradation_pct")} step={0.05} t="def-deg" />
          <FieldNum label="Discount rate %" v={s.default_discount_rate_pct} onChange={u("default_discount_rate_pct")} step={0.1} t="def-disc" />
        </div>
      </section>

      <section className="swiss-card-flat p-6 mb-6" data-testid="performance-section">
        <div className="label mb-4">Performance monitoring &amp; alerts</div>
        <div className="grid md:grid-cols-3 gap-5">
          <FieldNum label="Grid carbon factor (kg CO₂e/kWh)"
                    v={s.grid_carbon_factor_kg_per_kwh ?? 0.233}
                    onChange={u("grid_carbon_factor_kg_per_kwh")} step={0.001} t="perf-carbon-factor" />
          <FieldNum label="Underperformance threshold (%)"
                    v={s.underperformance_threshold_pct ?? 15}
                    onChange={u("underperformance_threshold_pct")} step={1} t="perf-threshold" />
          <label className="flex items-center gap-3 mt-6 cursor-pointer" data-testid="perf-alerts-toggle-label">
            <input type="checkbox" checked={!!s.performance_alerts_active}
                   onChange={(e) => setS((prev) => ({ ...prev, performance_alerts_active: e.target.checked }))}
                   data-testid="perf-alerts-toggle" />
            <span className="text-sm">Email daily underperformance alerts</span>
          </label>
        </div>
        <p className="text-xs muted mt-3">
          Flags days where actual production falls more than the threshold below the weather-adjusted
          expected yield. Alerts require an outbound email key to be configured.
        </p>
      </section>

      <section className="swiss-card-flat p-6 mb-6" data-testid="integrations-section">
        <div className="label mb-2">Gemini Vision API</div>
        <p className="text-xs muted mb-4">
          Unlocks SolarEdge URL/PDF design import. Get a key at{" "}
          <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="underline">aistudio.google.com</a>{" "}
          (free tier ≈ 1500 vision calls/day). Status: <strong>{s.gemini_api_key_set ? "Configured" : "Not set"}</strong>
        </p>
        <div className="flex gap-2 mb-6">
          <input
            data-testid="gemini-api-key-input"
            type="password"
            className="input-box flex-1 font-mono text-xs"
            placeholder={s.gemini_api_key_set ? "•••••••••••••••• (leave blank to keep current key)" : "Paste Gemini API key"}
            value={s.gemini_api_key || ""}
            onChange={u("gemini_api_key")}
            autoComplete="new-password"
          />
        </div>

        <div className="label mb-2">SolarEdge Monitoring API</div>
        <p className="text-xs muted mb-4">Global default — can be overridden per project. Status: <strong>{s.solaredge_api_key_set ? "Configured" : "Not set"}</strong></p>
        <div className="flex gap-2">
          <input
            data-testid="solaredge-api-key-input"
            type="password"
            className="input-box flex-1 font-mono text-xs"
            placeholder={s.solaredge_api_key_set ? "•••••••••••••••• (leave blank to keep current key)" : "Paste SolarEdge API key"}
            value={s.solaredge_api_key || ""}
            onChange={u("solaredge_api_key")}
            autoComplete="new-password"
          />
        </div>
      </section>

      <div className="flex justify-end mb-12">
        <button className="btn-primary" onClick={save} data-testid="settings-save-btn"><Save size={14} /> Save settings</button>
      </div>

      {isAdmin && <FeatureTogglesMatrix settings={s} setSettings={setS} save={save} />}

      {(user?.role === "admin" || user?.role === "manager") && <PmStageConfigSection />}

      {(user?.role === "admin" || user?.role === "manager") && <AssetManagersSection />}

      {(user?.role === "admin" || user?.role === "manager") && <MaintenanceVisitTypesSection />}

      {(user?.role === "admin" || user?.role === "manager") && <TaskTemplateSection />}

      {(user?.role === "admin" || user?.role === "manager") && <WeatherBackfillSection />}

      {isAdmin && (
        <section className="swiss-card-flat p-6" data-testid="env-section">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={14} />
            <div className="label">Server .env (admin only)</div>
          </div>
          <p className="muted text-xs mb-4">Edit selected backend environment variables. Updates are persisted to <code className="font-mono">/app/backend/.env</code> and applied to the running process.</p>
          <table className="data-table">
            <thead><tr><th>Key</th><th>Value</th><th></th></tr></thead>
            <tbody>
              {env.map(row => (
                <EnvRow key={row.key} row={row} onSave={saveEnv} />
              ))}
            </tbody>
          </table>
        </section>
      )}

      {(user?.role === "admin" || user?.role === "manager") && <ServerStatusSection />}
      {(user?.role === "admin" || user?.role === "manager") && <IntegrationStatus />}
      {(user?.role === "admin" || user?.role === "manager") && <WebhooksSection />}
      {isAdmin && <LifecycleSanitySection />}
      {isAdmin && <NotProceedingMigrationSection />}
      {isAdmin && <BackupSection />}
      {isAdmin && <PmBackupSection />}
    </div>
  );
}

function FieldText({ label, v, onChange, t, placeholder = "" }) {
  return (
    <label className="block">
      <span className="label block mb-1">{label}</span>
      <input className="input-swiss" value={v || ""} onChange={onChange}
             placeholder={placeholder} data-testid={t} />
    </label>
  );
}

function ColorPicker({ label, v, defaultColor, onChange, t }) {
  // Show the resolved colour (v OR the default) so the swatch/text-input never
  // render empty — but only persist `v` when the user has explicitly chosen
  // one. Clicking "Reset" clears the field so the server-side default kicks in.
  const shown = v || defaultColor;
  const clear = () => onChange("");
  return (
    <label className="block">
      <span className="label block mb-1">{label}</span>
      <div className="flex items-stretch gap-2">
        <input type="color" value={shown} onChange={(e) => onChange(e.target.value)}
               className="w-14 h-10 border border-[var(--line)] cursor-pointer p-0"
               data-testid={`${t}-swatch`} />
        <input type="text" value={v || ""} onChange={(e) => onChange(e.target.value)}
               placeholder={defaultColor}
               className="input-swiss flex-1 font-mono text-xs uppercase"
               data-testid={`${t}-hex`} />
        {v && (
          <button type="button" onClick={clear}
                  className="btn-ghost text-[10px]"
                  data-testid={`${t}-reset`}>Reset</button>
        )}
      </div>
    </label>
  );
}
function FieldNum({ label, v, onChange, step = 1, t }) {
  return (
    <label className="block">
      <span className="label block mb-1">{label}</span>
      <input className="input-swiss" type="number" step={step} value={v ?? 0} onChange={onChange} data-testid={t} />
    </label>
  );
}

function EnvRow({ row, onSave }) {
  const [v, setV] = useState("");
  return (
    <tr data-testid={`env-row-${row.key}`}>
      <td className="font-mono text-xs">{row.key}</td>
      <td>
        <input
          className="input-swiss font-mono text-xs w-full"
          placeholder={row.set ? (row.is_secret ? "(set — replace)" : row.value) : "(empty)"}
          value={v}
          onChange={(e) => setV(e.target.value)}
          data-testid={`env-input-${row.key}`}
        />
      </td>
      <td className="num">
        <button className="btn-ghost" onClick={() => { if (v) { onSave(row.key, v); setV(""); } }} data-testid={`env-save-${row.key}`}>Save</button>
      </td>
    </tr>
  );
}



/**
 * Feature toggle matrix — a role × feature grid of checkboxes. Admin-only
 * (rendered from Settings.jsx). Reads the catalog from `settings._feature_catalog`
 * (keys + roles arrays) so new features drop in automatically once the
 * backend `FEATURE_KEYS` constant is updated.
 *
 * Change model: edits mutate a local shadow copy of `feature_toggles`; the
 * "Save toggles" button PUTs the entire settings doc (reuses the existing
 * save flow so we don't double-manage form state).
 */
function FeatureTogglesMatrix({ settings, setSettings, save }) {
  const catalog = settings?._feature_catalog;
  const toggles = settings?.feature_toggles || {};
  if (!catalog) return null;
  const flip = (role, key) => {
    const next = { ...toggles };
    next[role] = { ...(next[role] || {}) };
    // Default is TRUE — so first click flips to FALSE. Once explicitly set,
    // the checkbox reflects the stored value.
    const current = next[role][key];
    next[role][key] = current === false ? true : false;
    setSettings({ ...settings, feature_toggles: next });
  };
  const isEnabled = (role, key) => {
    const v = toggles?.[role]?.[key];
    return v === false ? false : true;   // default = enabled
  };
  return (
    <section className="swiss-card-flat p-6 mb-8" data-testid="feature-toggles-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label">Feature toggles · access by role</div>
          <div className="muted text-xs mt-1">
            Untick to hide a feature for that role. Admin always has full access.
            Changes take effect after users refresh.
          </div>
        </div>
        <button onClick={save} className="btn-accent" data-testid="save-feature-toggles-btn">
          Save toggles
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table text-xs">
          <thead>
            <tr>
              <th className="text-left">Feature</th>
              {catalog.roles.map((r) => (
                <th key={r} className="text-center capitalize" data-testid={`feature-role-${r}`}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.keys.map((k) => (
              <tr key={k} data-testid={`feature-row-${k}`}>
                <td className="font-mono">{k}</td>
                {catalog.roles.map((r) => (
                  <td key={r} className="text-center">
                    <input type="checkbox"
                           checked={r === "admin" ? true : isEnabled(r, k)}
                           disabled={r === "admin"}
                           onChange={() => flip(r, k)}
                           data-testid={`toggle-${r}-${k}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
